import * as React from 'react';

type Props = {
  children: React.ReactElement;
};

/**
 * Session gate for BFF auth: probes the server's session once and either
 * renders the app or bounces through the server's /login route (which runs
 * the OAuth flow with Auth0 and redirects back to returnTo). The probe is a
 * same-origin fetch against our own server — tens of milliseconds, not the
 * cross-origin silent-auth round trip the old client-side flow paid on
 * every load.
 */
const AuthenticationRequired = (props: Props) => {
  const [authenticated, setAuthenticated] = React.useState<boolean | null>(
    null,
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/auth/me', {
          credentials: 'include',
        });
        const body: unknown = await response.json();
        const isAuthed =
          typeof body === 'object' &&
          body !== null &&
          'authenticated' in body &&
          body.authenticated === true;
        if (!cancelled) {
          setAuthenticated(isAuthed);
        }
      } catch (err) {
        console.error('[auth] Session probe failed:', err);
        if (!cancelled) {
          setAuthenticated(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (authenticated === false) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      window.location.assign(
        `/login?returnTo=${encodeURIComponent(returnTo)}`,
      );
    }
  }, [authenticated]);

  if (authenticated) {
    return props.children;
  }
  return null;
};

export default AuthenticationRequired;
